// Shared helpers for the FR (default, no prefix) / EN (/en prefix) URL
// structure. Every page keeps a single canonical FR path (e.g. "/",
// "/about", "/out-of-home") and these helpers derive everything else
// (the /en equivalent, which one the current URL is, canonical/hreflang
// URLs) from it, so that logic lives in one place instead of being
// re-implemented per page/navbar.

export const DOMAIN = "https://www.distributiondrinq.ca";

// True for "/en" and "/en/..." - false for every FR path.
export const isEnglishPath = (pathname) =>
  pathname === "/en" || pathname.startsWith("/en/");

// Removes a leading "/en" prefix, if present. Always returns a FR
// canonical path starting with "/" (e.g. "/en/about" -> "/about",
// "/en" -> "/", "/about" -> "/about").
export const toFrPath = (pathname) => {
  if (!isEnglishPath(pathname)) return pathname;
  const stripped = pathname.slice(3);
  return stripped === "" ? "/" : stripped;
};

// Adds the "/en" prefix to a FR canonical path (e.g. "/" -> "/en",
// "/about" -> "/en/about").
export const toEnPath = (frPath) => (frPath === "/" ? "/en" : `/en${frPath}`);

// FR paths that currently have a real, prerendered /en counterpart.
// City/service landing pages are FR-only for now (see OutOfHome.jsx's
// cityKey handling), so the navbar's EN/FR toggle needs to know not to
// send visitors to a /en/<slug> URL that doesn't exist yet.
const TRANSLATED_FR_PATHS = new Set(["/", "/about", "/get-started", "/out-of-home"]);

// Where the EN/FR toggle should go when clicking "EN" from a page that
// has no /en version yet - falls back to the closest translated page
// instead of a 404.
export const toEnPathOrFallback = (frPath, fallback = "/en/out-of-home") =>
  TRANSLATED_FR_PATHS.has(frPath) ? toEnPath(frPath) : fallback;

// Plain helper, not a hook - pass in location.pathname yourself (via
// useLocation()) so this can stay simple and testable.
// frPath: the page's canonical FR path (e.g. "/", "/about"). currentPathname:
// location.pathname, used only to tell which language is currently active.
export function getLangUrls(frPath, currentPathname) {
  const isEnglish = isEnglishPath(currentPathname);
  const frUrl = `${DOMAIN}${frPath}`;
  const enUrl = `${DOMAIN}${toEnPath(frPath)}`;
  return {
    lang: isEnglish ? "en" : "fr",
    canonical: isEnglish ? enUrl : frUrl,
    hreflangFr: frUrl,
    hreflangEn: enUrl,
    // Path (not full URL) to the current page in the *other* language -
    // for the navbar's EN/FR toggle.
    otherLangPath: isEnglish ? frPath : toEnPath(frPath),
  };
}
